import { resultRevisionKey } from '../utils/resultRevisions';
import FeatureAction from './FeatureAction';
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import type { ResultLinkTarget } from '../navigation/resultLinks';
import { useTabActivity } from '../contexts/TabActivityContext';
import { X, RefreshCw, AlertCircle, CheckCircle, Clock, Download, SortAsc, ChevronDown, ChevronUp, Minimize2, Maximize2, Search, Code, List } from 'lucide-react';
import { apiService, getApiErrorMessage, PromptRun, PromptRunWorkflowVideoResult, SearchResult, Video, VideoPromptRunResult } from '../services/api';
import { segmentCache, type SegmentData } from '../services/segmentCache';
import { applyRealtimeStateToVideo, processingRealtimeStore } from '../services/processingRealtimeStore';
import { usePassiveApiActivity } from '../hooks/usePassiveApiActivity';
import { isInProgressStatus, isVideoInProgress } from '../utils/realtimeStreamEligibility';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { getProcessingEventScopedVideoIds } from '../utils/processingEventVideoScope';
import { toPromptRunFromProcessingEvent } from '../utils/promptRunEventMapper';
import { getDisplayedVideoStatus, getRunScopedVideoProjection, getVideoProgressSummary, selectVideoRun } from '../utils/runScopedVideoStatus';
import ProcessingProgressBar from './ProcessingProgressBar';
import ImageEnrichmentStatus from './ImageEnrichmentStatus';
import ProcessingConnectionNotice from './ProcessingConnectionNotice';
import { SegmentedPlayer } from './SegmentedPlayer';
import { SegmentedTimeline } from './SegmentedTimeline';
import {
  arePlaybackSegmentsEquivalent,
  getTotalDuration,
  locateGlobalTime,
  prepareSegments,
  VideoSegment,
} from '../utils/segmentedVideo';
import { normalizePromptSelectionFilter, promptRunHasSelectionFilter } from '../features/workflows/config';
import { useResolvedMediaUrl } from '../hooks/useResolvedMediaUrl';
import GrantedPreviewImage from './GrantedPreviewImage';
import { SegmentPreviewGallery, SegmentPreviewGalleryItem } from './SegmentPreviewGallery';
import SegmentDownloadActions from './SegmentDownloadActions';
import { isRenderableMediaUri, pickDurableMediaUri } from '../utils/mediaSources';
import { downloadJsonFile, sanitizeDownloadFilenamePart } from '../utils/download';
import { buildStudioMetadataExport, buildStudioRunMetadata } from '../utils/studioMetadataExport';
import { resolveSegmentProcessingStatus } from '../utils/segmentStatus';
import { formatSearchScore, getHighestFieldSearchScore } from '../utils/searchScore';
import { getSearchResultRunId } from '../utils/searchResultIdentity';
import { getBestFieldScoreForDisplayPath } from '../utils/searchFieldScores';
import { createSearchFieldMatcher } from '../utils/searchFieldMatches';
import { formatRunSegmentationLabel } from '../utils/runSegmentation';
import type { PreviewGrantProjection } from '../contexts/SearchWorkspaceContext';
import StructuredMetadataView, { getCollapsibleMetadataPaths } from './StructuredMetadataView';
import StudioResultToolbar from './StudioResultToolbar';
import { getStudioConditionFields, getStudioMatchedFieldPaths, selectStudioResults, studioSearchText } from '../features/playground/studioResultFiltering';
import {
  DEFAULT_PLAYGROUND_STUDIO_WORKSPACE_STATE,
  PlaygroundStudioWorkspaceState,
} from '../features/playground/studioWorkspaceState';
import './VideoDetailModal.css';
import './WorkflowRunResults.css';

type SortDirection = 'asc' | 'desc';
type MetadataViewMode = 'fields' | 'json';
type VideoDetailPresentation = 'sidebar' | 'embedded';
type VideoDetailPlaybackMode = 'segments' | 'continuous-video';
const EMPTY_SEGMENT_IDS: readonly string[] = [];

const ResultPlaceholder: React.FC<{ title: string; children?: React.ReactNode; busy?: boolean }> = ({ title, children, busy }) => (
  <div className="result-placeholder" role="status">
    {busy ? <RefreshCw size={22} className="spinning" aria-hidden="true" /> : <Search size={22} aria-hidden="true" />}
    <strong>{title}</strong>
    {children && <p>{children}</p>}
  </div>
);


interface VideoDetailModalProps {
  video: Video | null;
  isOpen: boolean;
  isVisible?: boolean;
  onClose: () => void;
  presentation?: VideoDetailPresentation;
  playbackMode?: VideoDetailPlaybackMode;
  /** Index owns its execution catalogue and scope; standalone viewers load their own. */
  runCatalog?: readonly PromptRun[];
  runScopeId?: string | null;
  indexId?: string;
  /** Null/undefined shows every run segment; an array is an exact visible segment scope. */
  visibleSegmentIds?: readonly string[] | null;
  /** False preserves parent-media context without exposing an unmatched video result. */
  showVideoLevelOutput?: boolean;
  deepLinkRunId?: string | null;
  resultLink?: ResultLinkTarget | null;
  resultSelectionPending?: boolean;
  onResultLinkChange?: (patch: Partial<ResultLinkTarget>) => void;
  onRunSelectionChange?: (
    runId: string | null,
    options?: { userInitiated?: boolean },
  ) => void;
  deepLinkTime?: number | null;
  deepLinkFieldKey?: string | null;
  onPlaybackTimeChange?: (time: number) => void;
  workspaceState?: PlaygroundStudioWorkspaceState;
  onWorkspaceStateChange?: (patch: Partial<PlaygroundStudioWorkspaceState>) => void;
  embeddedHeaderActionsTarget?: HTMLElement | null;
  /** Search owns the execution and navigation; ordinary Studio retains its run selector. */
  searchContext?: { results: SearchResult[]; navigation: React.ReactNode };
}

const GrantedSegmentPreview: React.FC<{
  segment: SegmentData;
  videoId: string;
  alt: string;
  fallback: React.ReactNode;
}> = ({ segment, videoId, alt, fallback }) => {
  const thumbnailGcsUri = pickDurableMediaUri(segment.thumbnail_gcs_uri);
  const gifGcsUri = pickDurableMediaUri(segment.gif_gcs_uri);
  const projection: PreviewGrantProjection | undefined = thumbnailGcsUri || gifGcsUri
    ? {
        identity: {
          kind: 'segment',
          video_id: videoId,
          segment_id: segment.segment_id,
        },
        thumbnailGcsUri,
        gifGcsUri,
      }
    : undefined;
  return (
    <GrantedPreviewImage
      posterUrl={segment.thumbnail_uri}
      animatedUrl={segment.gif_uri}
      projection={projection}
      alt={alt}
      className="segment-gif"
      containerClassName="granted-segment-preview"
      engaged
      placeholder={fallback}
    />
  );
};

// Helper to detect if content is an image from filename/title
const isImageFile = (filename: string): boolean => {
  const ext = filename.toLowerCase().split('.').pop() || '';
  return ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tiff', 'heic', 'heif'].includes(ext);
};

const isImageVideo = (candidate: Pick<Video, 'title' | 'media_type'>): boolean => (
  String(candidate.media_type || '').trim().toLowerCase() === 'image'
  || isImageFile(candidate.title)
);

const VideoDetailModal: React.FC<VideoDetailModalProps> = ({
  video,
  isOpen,
  isVisible = true,
  onClose,
  presentation = 'sidebar',
  playbackMode: defaultPlaybackMode = 'segments',
  runCatalog,
  runScopeId,
  indexId,
  visibleSegmentIds = null,
  showVideoLevelOutput: requestedShowVideoLevelOutput = true,
  deepLinkRunId = null,
  resultLink = null,
  resultSelectionPending = false,
  onResultLinkChange,
  onRunSelectionChange,
  deepLinkTime = null,
  deepLinkFieldKey = null,
  onPlaybackTimeChange,
  workspaceState,
  onWorkspaceStateChange,
  embeddedHeaderActionsTarget = null,
  searchContext,
}) => {
  const playbackMode = resultLink?.playback === 'video' ? 'continuous-video'
    : resultLink?.playback === 'segment' ? 'segments' : defaultPlaybackMode;
  const isEmbeddedPresentation = presentation === 'embedded';
  const runDropdownId = useId();
  const isStudio = isEmbeddedPresentation && visibleSegmentIds == null;
  const { isActive } = useTabActivity();
  const isPresentationVisible = isActive && isOpen && isVisible;
  const videoId = video?.video_id || null;
  const videoIsImage = video ? isImageVideo(video) : false;
  const [availableRuns, setAvailableRuns] = useState<PromptRun[]>([]);
  const [liveVideoState, setLiveVideoState] = useState<ReturnType<typeof processingRealtimeStore.getVideoState>>();
  const snapshotResultVersionsRef = useRef(new Map<string, string>());
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const selectedRun = selectedRunId
    ? availableRuns.find((run) => run.run_id === selectedRunId) || null
    : null;
  const studioSelectionKey = `${videoId}:${selectedRunId}`;
  const [studioResultMode, setStudioResultMode] = useState<{
    key: string; mode: 'matches' | 'all';
  } | null>(null);
  const [studioSelection, setStudioSelection] = useState<{
    key: string; row: PromptRunWorkflowVideoResult | null; error: string | null;
  } | null>(null);
  const studioHasFilter = !searchContext && isEmbeddedPresentation && visibleSegmentIds == null
    && promptRunHasSelectionFilter(selectedRun);
  const studioShowsMatches = studioHasFilter
    && (studioResultMode?.key === studioSelectionKey ? studioResultMode.mode : resultLink?.resultScope || 'all') === 'matches';
  const studioSelectionReady = selectedRun?.selection_summary?.status === 'ready';
  const currentStudioSelection = studioSelection?.key === studioSelectionKey ? studioSelection : null;
  const effectiveVisibleSegmentIds = studioShowsMatches
    ? currentStudioSelection?.row?.matched_segment_ids || EMPTY_SEGMENT_IDS
    : visibleSegmentIds;
  const showVideoLevelOutput = requestedShowVideoLevelOutput
    && (!studioShowsMatches || (studioSelectionReady && Boolean(currentStudioSelection?.row?.video_matched)));

  useEffect(() => {
    if (!studioSelectionReady && currentStudioSelection) {
      setStudioSelection(null);
      return;
    }
    if (!isPresentationVisible || !studioShowsMatches || !studioSelectionReady
      || !selectedRunId || !videoId || currentStudioSelection) return;
    const controller = new AbortController();
    void apiService.getPromptRunWorkflowResults(selectedRunId, 'video', 1, undefined, {
      videoId,
      signal: controller.signal,
    }).then((page) => {
      if (controller.signal.aborted) return;
      const row = page.data.find((item): item is PromptRunWorkflowVideoResult => (
        'matched_segment_ids' in item && item.video_id === videoId
      )) || null;
      setStudioSelection({ key: studioSelectionKey, row, error: null });
    }).catch((loadError) => {
      if (!controller.signal.aborted) {
        setStudioSelection({
          key: studioSelectionKey,
          row: null,
          error: getApiErrorMessage(loadError, 'Could not load matches. Turn off the engine filter to browse all extracted results.'),
        });
      }
    });
    return () => controller.abort();
  }, [currentStudioSelection, isPresentationVisible, selectedRunId, studioSelectionKey, studioSelectionReady, studioShowsMatches, videoId]);

  const normalizedVisibleSegmentIds = useMemo(() => (
    effectiveVisibleSegmentIds === null || effectiveVisibleSegmentIds === undefined
      ? null
      : Array.from(new Set(
          effectiveVisibleSegmentIds
            .map((segmentId) => String(segmentId || '').trim())
            .filter(Boolean),
        )).sort()
  ), [effectiveVisibleSegmentIds]);
  const visibleSegmentScopeKey = normalizedVisibleSegmentIds === null
    ? 'all'
    : `only:${JSON.stringify(normalizedVisibleSegmentIds)}`;
  const visibleSegmentIdSet = useMemo(
    () => normalizedVisibleSegmentIds === null ? null : new Set(normalizedVisibleSegmentIds),
    [normalizedVisibleSegmentIds],
  );
  const initialWorkspaceState = workspaceState?.scopeVideoId === videoId
    && workspaceState?.scopeRunId === selectedRunId
    ? workspaceState : DEFAULT_PLAYGROUND_STUDIO_WORKSPACE_STATE;
  const [segments, setSegments] = useState<SegmentData[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<SegmentData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [lastLoadedRunId, setLastLoadedRunId] = useState<string | null>(null);
  const [lastLoadedSegmentScopeKey, setLastLoadedSegmentScopeKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadErrorKind, setLoadErrorKind] = useState<'runs' | 'segments' | null>(null);
  const loadErrorKindRef = useRef<'runs' | 'segments' | null>(null);
  const currentSegmentScopeKey = videoId
    ? `${videoId}:${selectedRunId || 'no-run'}:${visibleSegmentScopeKey}`
    : null;
  const studioResultsUnavailable = studioShowsMatches
    && (!studioSelectionReady || !currentStudioSelection || Boolean(currentStudioSelection.error)
      || currentSegmentScopeKey !== lastLoadedSegmentScopeKey);
  const [videoLevelResult, setVideoLevelResult] = useState<VideoPromptRunResult | null>(null);
  const studioRunFilter = useMemo(() => normalizePromptSelectionFilter(selectedRun?.selection_filter), [selectedRun?.selection_filter]);
  const studioVideoMetadata = showVideoLevelOutput && !videoIsImage
    && videoLevelResult?.run_id === selectedRunId && videoLevelResult?.video_id === videoId
    && videoLevelResult?.status === 'successful' ? videoLevelResult.metadata : null;
  const studioSegmentFields = useMemo(() => isStudio
    ? getStudioConditionFields(segments.map((segment) => segment.metadata)) : [], [isStudio, segments]);
  const studioVideoFields = useMemo(() => isStudio && studioVideoMetadata
    ? getStudioConditionFields([studioVideoMetadata]) : [], [isStudio, studioVideoMetadata]);
  const studioSearchIndex = useMemo(() => isStudio ? {
    segments: new Map(segments.map((segment) => [segment.segment_id, studioSearchText(segment.metadata, segment.metadata_text)])),
    video: studioSearchText(studioVideoMetadata, videoLevelResult?.metadata_text),
  } : null, [isStudio, segments, studioVideoMetadata, videoLevelResult?.metadata_text]);
  const localResultSelection = useMemo(() => {
    if (!isStudio || searchContext) return null;
    const custom = selectStudioResults(segments, studioVideoMetadata, initialWorkspaceState.resultFilter);
    const query = initialWorkspaceState.resultSearchTerm.trim().replace(/_/g, ' ').toLocaleLowerCase();
    const loaded = currentSegmentScopeKey === lastLoadedSegmentScopeKey;
    return {
      segmentIds: new Set(segments.filter((segment) => loaded && custom.segmentIds.has(segment.segment_id)
        && (!query || studioSearchIndex?.segments.get(segment.segment_id)?.includes(query))
      ).map((segment) => segment.segment_id)),
      videoMatched: custom.videoMatched && (!query || Boolean(studioSearchIndex?.video.includes(query))),
    };
  }, [isStudio, searchContext, segments, studioVideoMetadata, initialWorkspaceState.resultFilter,
    initialWorkspaceState.resultSearchTerm, studioSearchIndex, currentSegmentScopeKey, lastLoadedSegmentScopeKey]);
  const displayedSegments = useMemo(() => localResultSelection
    ? segments.filter((segment) => localResultSelection.segmentIds.has(segment.segment_id)) : segments,
  [localResultSelection, segments]);
  const searchHitsBySegmentId = useMemo(() => new Map(
    (searchContext?.results || []).filter((result) => result.video_id === videoId
      && getSearchResultRunId(result) === selectedRunId && result.segment_id)
      .map((result) => [result.segment_id!, result]),
  ), [searchContext?.results, videoId, selectedRunId]);
  const selectedSegmentHit = selectedSegment ? searchHitsBySegmentId.get(selectedSegment.segment_id) : undefined;
  const selectedVideoHit = useMemo(() => searchContext?.results.find((result) => (
    result.result_type === 'video' && result.video_id === videoId
    && getSearchResultRunId(result) === selectedRunId
  )), [searchContext?.results, videoId, selectedRunId]);
  const localMatchedFields = useMemo(() => {
    const fields = { segment: new Set<string>(), video: new Set<string>() };
    if (!localResultSelection) return fields;
    (['segment', 'video'] as const).forEach((level) => {
      const metadata = level === 'segment' ? selectedSegment?.metadata : studioVideoMetadata;
      fields[level] = getStudioMatchedFieldPaths(
        metadata, initialWorkspaceState.resultFilter?.[level], initialWorkspaceState.resultSearchTerm,
      );
      if (studioShowsMatches) {
        getStudioMatchedFieldPaths(metadata, studioRunFilter?.[level], '').forEach((path) => fields[level].add(path));
      }
    });
    return fields;
  }, [localResultSelection, selectedSegment?.metadata, studioVideoMetadata, initialWorkspaceState.resultFilter,
    initialWorkspaceState.resultSearchTerm, studioShowsMatches, studioRunFilter]);
  const segmentSearchFieldMatcher = useMemo(() => createSearchFieldMatcher(selectedSegmentHit), [selectedSegmentHit]);
  const videoSearchFieldMatcher = useMemo(() => createSearchFieldMatcher(selectedVideoHit), [selectedVideoHit]);
  const selectedSegmentIsVisible = !localResultSelection
    || Boolean(selectedSegment && localResultSelection.segmentIds.has(selectedSegment.segment_id));
  const videoOutputHidden = Boolean(localResultSelection && studioVideoMetadata && !localResultSelection.videoMatched);
  const exportSegmentIdSet = localResultSelection?.segmentIds || visibleSegmentIdSet;
  const exportVideoLevelOutput = showVideoLevelOutput && (!localResultSelection || localResultSelection.videoMatched);
  const preparedVideoSegments = useMemo(
    () => prepareSegments(
      isOpen && currentSegmentScopeKey === lastLoadedSegmentScopeKey
        ? displayedSegments
        : [],
      videoId,
    ),
    [currentSegmentScopeKey, displayedSegments, isOpen, lastLoadedSegmentScopeKey, videoId],
  );
  const stableVideoSegmentsRef = useRef<VideoSegment[]>([]);
  const videoSegments = arePlaybackSegmentsEquivalent(
    stableVideoSegmentsRef.current,
    preparedVideoSegments,
  )
    ? stableVideoSegmentsRef.current
    : preparedVideoSegments;
  stableVideoSegmentsRef.current = videoSegments;
  const videoTimelineDuration = getTotalDuration(videoSegments);
  const continuousPlaybackVideoId = video?.video_id || '';
  const continuousPlaybackSourceUri = String(video?.video_uri || '').trim();
  const continuousPlaybackDeclaredDuration = Number(video?.duration_seconds);
  const continuousPlaybackDuration = Number.isFinite(continuousPlaybackDeclaredDuration) && continuousPlaybackDeclaredDuration > 0
    ? continuousPlaybackDeclaredDuration : videoTimelineDuration;

  const continuousPlaybackSegments = useMemo<VideoSegment[]>(() => {
    // Source playback is independent of extraction and derivative readiness.
    if (
      playbackMode !== 'continuous-video'
      || !isOpen
      || !continuousPlaybackVideoId
      || videoIsImage
    ) return [];
    const duration = continuousPlaybackDuration;
    if (!Number.isFinite(duration) || duration <= 0) return [];

    if (!continuousPlaybackSourceUri) return [];
    return [{
      id: `video-source:${continuousPlaybackVideoId}`,
      videoId: continuousPlaybackVideoId,
      mediaScope: 'video',
      playbackUrl: isRenderableMediaUri(continuousPlaybackSourceUri) ? continuousPlaybackSourceUri : '',
      previewGifUrl: '',
      duration,
      start: 0,
      end: duration,
      originalUri: continuousPlaybackSourceUri,
    }];
  }, [
    continuousPlaybackDuration,
    continuousPlaybackSourceUri,
    continuousPlaybackVideoId,
    isOpen,
    playbackMode,
    videoIsImage,
  ]);
  const playerPlaybackSegments = playbackMode === 'continuous-video'
    ? continuousPlaybackSegments
    : videoSegments;
  const playbackTimelineDuration = playbackMode === 'continuous-video'
    ? continuousPlaybackSegments[0]?.duration || videoTimelineDuration
    : videoTimelineDuration;
  const [promptRunsLoadedForVideoId, setPromptRunsLoadedForVideoId] = useState<string | null>(null);
  const [showRunDropdown, setShowRunDropdown] = useState(false);
  const [isLoadingVideoLevelResult, setIsLoadingVideoLevelResult] = useState(false);
  const [videoLevelResultError, setVideoLevelResultError] = useState<string | null>(null);
  const isPageVisible = usePageVisibility();
  const canObserve = usePassiveApiActivity(isVideoInProgress(video), isPresentationVisible);
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (presentation === 'embedded') return 900;
    const saved = localStorage.getItem('videoSidebarWidth');
    return saved ? parseInt(saved, 10) : 900;
  });
  const [isResizing, setIsResizing] = useState(false);
  const selectedThumbnailSource = selectedSegment?.thumbnail_uri || null;
  const selectedThumbnailGcsUri = pickDurableMediaUri(selectedSegment?.thumbnail_gcs_uri);
  const selectedGifGcsUri = pickDurableMediaUri(selectedSegment?.gif_gcs_uri);
  const selectedThumbnailProjection: PreviewGrantProjection | undefined = (
    selectedSegment
    && videoId
    && (selectedThumbnailGcsUri || selectedGifGcsUri)
  )
    ? {
        identity: {
          kind: 'segment',
          video_id: videoId,
          segment_id: selectedSegment.segment_id,
        },
        thumbnailGcsUri: selectedThumbnailGcsUri,
        gifGcsUri: selectedGifGcsUri,
      }
    : undefined;
  const fullImageSource = (
    isOpen && video && videoIsImage
      ? video.video_uri
      : null
  );
  const {
    resolvedUrl: fullImageUrl,
    isLoading: isLoadingFullImage,
    refreshAfterError: refreshFullImageAfterError,
  } = useResolvedMediaUrl(fullImageSource, { videoId: video?.video_id });

  // Metadata display controls
  const [metadataSortDir, setMetadataSortDir] = useState<SortDirection>(
    initialWorkspaceState.metadataSortDirection,
  );
  const [metadataSearchTerm, setMetadataSearchTerm] = useState(
    initialWorkspaceState.metadataSearchTerm,
  );
  const [metadataViewMode, setMetadataViewMode] = useState<MetadataViewMode>(
    initialWorkspaceState.metadataViewMode,
  );
  const [collapsedMetadataPaths, setCollapsedMetadataPaths] = useState<Set<string>>(
    new Set(initialWorkspaceState.collapsedMetadataPaths),
  );
  const allFieldsExpanded = collapsedMetadataPaths.size === 0;
  const [videoLevelViewMode, setVideoLevelViewMode] = useState<MetadataViewMode>(
    initialWorkspaceState.videoLevelViewMode,
  );
  const [collapsedVideoLevelPaths, setCollapsedVideoLevelPaths] = useState<Set<string>>(
    new Set(initialWorkspaceState.collapsedVideoLevelPaths),
  );

  // Segmented player state
  const [globalTime, setGlobalTime] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(initialWorkspaceState.muted);
  const [playbackVolume, setPlaybackVolume] = useState<number>(initialWorkspaceState.volume);

  const modalRef = useRef<HTMLDivElement>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);
  const runDropdownRef = useRef<HTMLDivElement>(null);
  const availableRunsRef = useRef<PromptRun[]>([]);
  const allowZeroTimeSelectionRef = useRef(false);
  const videoLevelRequestIdRef = useRef(0);
  const videoLevelRequestRef = useRef<{
    scopeKey: string;
    controller: AbortController;
    promise: Promise<void>;
  } | null>(null);
  const promptRunsRequestIdRef = useRef(0);
  const segmentsRequestIdRef = useRef(0);
  const loadingSegmentScopeKeyRef = useRef<string | null>(null);
  const wasPageVisibleRef = useRef(isPageVisible);
  const selectedRunIdRef = useRef<string | null>(selectedRunId);
  const selectedVideoIdRef = useRef<string | null>(video?.video_id || null);
  const visibleSegmentScopeKeyRef = useRef(visibleSegmentScopeKey);
  const isOpenRef = useRef(isOpen);
  const isPlaybackVisibleRef = useRef(isPresentationVisible);
  const shouldAutoplaySegmentPlayerRef = useRef(false);
  const initializedVideoIdRef = useRef<string | null>(null);
  const loadPromptRunsRef = useRef<(
    forceRefresh?: boolean,
  ) => Promise<PromptRun[]>>(
    async () => []
  );
  const loadSegmentsRef = useRef<(
    forceRefresh?: boolean,
  ) => Promise<void>>(async () => undefined);
  const loadVideoLevelResultRef = useRef<(
    runId: string,
    options?: { silent?: boolean; force?: boolean }
  ) => Promise<void>>(async () => undefined);

  const cancelVideoLevelRequest = useCallback(() => {
    videoLevelRequestRef.current?.controller.abort();
    videoLevelRequestRef.current = null;
    videoLevelRequestIdRef.current += 1;
  }, []);

  const refreshPromptRuns = useCallback(
    (forceRefresh = false) => (
      loadPromptRunsRef.current(forceRefresh)
    ),
    []
  );
  const refreshSegments = useCallback(
    (forceRefresh = false) => loadSegmentsRef.current(forceRefresh),
    []
  );
  const refreshVideoLevelResult = useCallback(
    (runId: string, options: { silent?: boolean; force?: boolean } = {}) => (
      loadVideoLevelResultRef.current(runId, options)
    ),
    []
  );

  selectedRunIdRef.current = selectedRunId;
  selectedVideoIdRef.current = videoId;
  visibleSegmentScopeKeyRef.current = visibleSegmentScopeKey;
  isOpenRef.current = isOpen;
  isPlaybackVisibleRef.current = isPresentationVisible;

  useEffect(() => () => {
    cancelVideoLevelRequest();
  }, [cancelVideoLevelRequest]);

  const deepLinkAppliedVideoIdRef = useRef<string | null>(null);
  const deepLinkRunAppliedRef = useRef<string | null>(null);
  const deepLinkTimeAppliedRef = useRef(false);
  const deepLinkFieldAppliedRef = useRef<string | null>(null);
  const [resultLinkError, setResultLinkError] = useState<string | null>(null);
  const localResultLinkChangeRef = useRef(false);
  const changeResultLink = (patch: Partial<ResultLinkTarget>) => {
    if (!onResultLinkChange) return;
    localResultLinkChangeRef.current = true;
    onResultLinkChange(patch);
  };
  const appliedResultLinkRef = useRef<string | null>(null);
  const preparedResultLinkRef = useRef<string | null>(null);
  const explicitSegmentTimeRef = useRef<number | null>(null);
  const requestedResultKey = resultLink && (resultLink.segmentId || resultLink.output || resultLink.playback || resultLink.metadata || resultLink.resultScope || resultLink.time !== null || resultLink.field)
    ? JSON.stringify(resultLink) : null;
  const [highlightFieldKey, setHighlightFieldKey] = useState<string | null>(null);
  const highlightedMetadataRowRef = useRef<HTMLDivElement | null>(null);
  const appliedWorkspaceScopeRef = useRef<string | null>(null);

  const updateWorkspaceState = useCallback((
    patch: Partial<PlaygroundStudioWorkspaceState>,
  ) => {
    onWorkspaceStateChange?.(patch);
  }, [onWorkspaceStateChange]);

  useEffect(() => {
    if (!isOpen || !workspaceState) return;
    const scopeKey = `${workspaceState.scopeVideoId || ''}\u0000${workspaceState.scopeRunId || ''}`;
    if (appliedWorkspaceScopeRef.current === scopeKey) return;
    appliedWorkspaceScopeRef.current = scopeKey;
    setMetadataSortDir(workspaceState.metadataSortDirection);
    setMetadataSearchTerm(workspaceState.metadataSearchTerm);
    setMetadataViewMode(workspaceState.metadataViewMode);
    setCollapsedMetadataPaths(new Set(workspaceState.collapsedMetadataPaths));
    setVideoLevelViewMode(workspaceState.videoLevelViewMode);
    setCollapsedVideoLevelPaths(new Set(workspaceState.collapsedVideoLevelPaths));
    setIsMuted(workspaceState.muted);
    setPlaybackVolume(workspaceState.volume);
  }, [isOpen, workspaceState]);

  // A fresh explicit URL overrides retained workspace preferences once. The
  // complete request key also handles Back/Forward within the same video/run.
  useEffect(() => {
    if (!isOpen || !requestedResultKey) {
      preparedResultLinkRef.current = null;
      appliedResultLinkRef.current = null;
      explicitSegmentTimeRef.current = null;
      setResultLinkError(null);
      return;
    }
    if (preparedResultLinkRef.current === requestedResultKey) {
      localResultLinkChangeRef.current = false;
      return;
    }
    preparedResultLinkRef.current = requestedResultKey;
    appliedResultLinkRef.current = null;
    deepLinkTimeAppliedRef.current = false;
    setResultLinkError(null);
    if (resultLink?.resultScope) setStudioResultMode({ key: studioSelectionKey, mode: resultLink.resultScope });
    if (resultLink?.field) {
      setCollapsedMetadataPaths(new Set());
      setCollapsedVideoLevelPaths(new Set());
    }
    if (resultLink?.metadata) {
      setMetadataViewMode(resultLink.metadata);
      setVideoLevelViewMode(resultLink.metadata);
    }
    const localChange = localResultLinkChangeRef.current;
    localResultLinkChangeRef.current = false;
    if (!localChange) {
      setMetadataSearchTerm('');
      updateWorkspaceState({
        resultSearchTerm: '', resultFilter: null, metadataSearchTerm: '',
        ...(resultLink?.field ? { collapsedMetadataPaths: [], collapsedVideoLevelPaths: [] } : {}),
        ...(resultLink?.metadata ? { metadataViewMode: resultLink.metadata, videoLevelViewMode: resultLink.metadata } : {}),
      });
    }
  }, [isOpen, requestedResultKey, resultLink, studioSelectionKey, updateWorkspaceState]);

  useEffect(() => {
    if (!isOpen || !requestedResultKey || !resultLink || appliedResultLinkRef.current === requestedResultKey) return;
    if (resultSelectionPending || studioResultsUnavailable) return;
    if (isStudio && studioHasFilter && resultLink.resultScope && resultLink.resultScope !== (studioShowsMatches ? 'matches' : 'all')) return;
    if (videoId !== resultLink.videoId || (resultLink.runId && selectedRunId !== resultLink.runId)) return;
    if (promptRunsLoadedForVideoId !== videoId || isLoadingRuns || isLoading || currentSegmentScopeKey !== lastLoadedSegmentScopeKey) return;
    const requestedSegment = resultLink.segmentId
      ? segments.find((segment) => segment.segment_id === resultLink.segmentId) : null;
    // The run-scoped segment endpoint is authoritative; the run catalogue
    // can briefly omit a newly admitted or refreshed execution.
    if (resultLink.output === 'video' && !selectedRun) return;
    if ((resultLink.segmentId && !requestedSegment)
      || (resultLink.output === 'video' && !selectedRun?.video_level_enabled)) {
      setResultLinkError('The requested result is no longer available in this execution.');
      setSelectedSegment(null);
      setIsPlaying(false);
      appliedResultLinkRef.current = requestedResultKey;
      return;
    }
    if (requestedSegment) {
      const requestedTime = resultLink.time;
      const time = requestedTime !== null && requestedTime >= requestedSegment.start_time && requestedTime < requestedSegment.end_time
        ? requestedTime : requestedSegment.start_time;
      explicitSegmentTimeRef.current = time;
      setSelectedSegment(requestedSegment);
      updateWorkspaceState({ selectedSegmentId: requestedSegment.segment_id });
      setGlobalTime(time);
      deepLinkTimeAppliedRef.current = true;
    } else if (resultLink.time !== null) {
      setGlobalTime(resultLink.time);
      deepLinkTimeAppliedRef.current = true;
    } else if (resultLink.playback === 'video') {
      setGlobalTime(0);
      allowZeroTimeSelectionRef.current = true;
      deepLinkTimeAppliedRef.current = true;
    }
    appliedResultLinkRef.current = requestedResultKey;
    setResultLinkError(null);
    if (!searchContext) {
      const panel = resultLink.output === 'video' ? '.video-level-panel' : '.segment-details-pane';
      modalRef.current?.querySelector<HTMLElement>(panel)?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [availableRuns, currentSegmentScopeKey, isLoading, isLoadingRuns, isOpen, lastLoadedSegmentScopeKey,
    isStudio, resultSelectionPending, studioHasFilter, studioResultsUnavailable, studioShowsMatches,
    promptRunsLoadedForVideoId, requestedResultKey, resultLink, searchContext, segments, selectedRunId, selectedRun, updateWorkspaceState, videoId]);

  // Always pause media before the shared detail surface closes, including when
  // a pending segment load attempts to start autoplay during that transition.
  useEffect(() => {
    if (isPresentationVisible) return;
    shouldAutoplaySegmentPlayerRef.current = false;
    if (isPlaying) setIsPlaying(false);
    setIsResizing(false);
  }, [isPlaying, isPresentationVisible]);

  useEffect(() => {
    if (isStudio && playerPlaybackSegments.length === 0) setIsPlaying(false);
  }, [isStudio, playerPlaybackSegments.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (runDropdownRef.current && !runDropdownRef.current.contains(event.target as Node)) {
        setShowRunDropdown(false);
      }
    };

    if (showRunDropdown && isPresentationVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRunDropdown, isPresentationVisible]);

  useEffect(() => {
    availableRunsRef.current = availableRuns;
  }, [availableRuns]);

  const getOperationStatusIcon = useCallback((status: boolean | null | undefined, size: number = 11) => {
    if (status === null || status === undefined) return <Clock size={size} className="status-pending" />;
    if (status === true) return <CheckCircle size={size} className="status-success" />;
    return <AlertCircle size={size} className="status-failed" />;
  }, []);

  // Sync selected segment with global time changes
  useEffect(() => {
    if (segments.length === 0 || videoSegments.length === 0 || resultLinkError) return;
    if (explicitSegmentTimeRef.current === globalTime) return;
    explicitSegmentTimeRef.current = null;
    if (globalTime > 0 || allowZeroTimeSelectionRef.current) {
      const playbackSegment = locateGlobalTime(globalTime, videoSegments).segment;
      const currentSegment = segments.find(
        (segment) => segment.segment_id === playbackSegment.id,
      );

      if (currentSegment) {
        setSelectedSegment((previousSegment) => (
          previousSegment?.segment_id === currentSegment.segment_id
            ? previousSegment
            : currentSegment
        ));
      }

      if (allowZeroTimeSelectionRef.current) {
        allowZeroTimeSelectionRef.current = false;
      }
    }
  }, [globalTime, segments, videoSegments, resultLinkError]);

  // Track deep-link consumption boundaries (per video open).
  useEffect(() => {
    if (!isOpen || !videoId) {
      deepLinkAppliedVideoIdRef.current = null;
      deepLinkRunAppliedRef.current = null;
      deepLinkTimeAppliedRef.current = false;
      deepLinkFieldAppliedRef.current = null;
      setHighlightFieldKey(null);
      return;
    }

    if (deepLinkAppliedVideoIdRef.current !== videoId) {
      deepLinkAppliedVideoIdRef.current = videoId;
      deepLinkRunAppliedRef.current = null;
      deepLinkTimeAppliedRef.current = false;
      deepLinkFieldAppliedRef.current = null;
      setHighlightFieldKey(null);
    }
  }, [isOpen, videoId]);

  // Apply the deep link once, and keep externally scoped matches on their owning run.
  // Time application waits for segment load.
  useEffect(() => {
    if (!isOpen || !videoId) return;
    if (!deepLinkRunId) return;
    if (
      deepLinkRunAppliedRef.current === deepLinkRunId
      && (visibleSegmentIds == null || selectedRunId === deepLinkRunId)
    ) return;

    deepLinkRunAppliedRef.current = deepLinkRunId;
    deepLinkTimeAppliedRef.current = false;
    if (selectedRunId !== deepLinkRunId) {
      selectedRunIdRef.current = deepLinkRunId;
      setSelectedRunId(deepLinkRunId);
    }
  }, [deepLinkRunId, isOpen, selectedRunId, videoId, visibleSegmentIds]);

  // Apply deep-link field highlight once per video-open.
  useEffect(() => {
    if (!isOpen || !videoId) return;
    if (!deepLinkFieldKey) return;
    if (deepLinkFieldAppliedRef.current === deepLinkFieldKey) return;

    deepLinkFieldAppliedRef.current = deepLinkFieldKey;
    setHighlightFieldKey(deepLinkFieldKey);
  }, [deepLinkFieldKey, isOpen, videoId]);

  // Apply deep-link time once segments for the current run are loaded.
  useEffect(() => {
    if (!isOpen || !videoId) return;
    if (deepLinkTime === null || deepLinkTime === undefined || resultLink?.segmentId) return;
    if (deepLinkTimeAppliedRef.current) return;
    if (playbackMode !== 'continuous-video' && (isLoading || segments.length === 0)) return;

    const readyForRun = (() => {
      if (playbackMode === 'continuous-video') return playerPlaybackSegments.length > 0;
      if (deepLinkRunId) {
        if (selectedRunId) {
          return lastLoadedRunId === selectedRunId;
        }
        // If there are no runs for this video, allow base segments.
        return availableRuns.length === 0 && !isLoadingRuns && lastLoadedRunId === null;
      }
      return lastLoadedRunId === selectedRunId;
    })();

    if (!readyForRun) return;

    if (deepLinkTime === 0) {
      allowZeroTimeSelectionRef.current = true;
    }
    setGlobalTime(deepLinkTime);
    deepLinkTimeAppliedRef.current = true;
  }, [
    availableRuns.length,
    deepLinkRunId,
    resultLink?.segmentId,
    deepLinkTime,
    isLoading,
    isLoadingRuns,
    isOpen,
    playbackMode,
    playerPlaybackSegments.length,
    lastLoadedRunId,
    segments.length,
    selectedRunId,
    videoId,
  ]);

  // Scroll highlighted field into view once metadata is rendered.
  useEffect(() => {
    if (!isOpen) return;
    if (!highlightFieldKey) return;
    if (!selectedSegment?.metadata) return;

    const el = highlightedMetadataRowRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [
    allFieldsExpanded,
    highlightFieldKey,
    isOpen,
    metadataSearchTerm,
    metadataSortDir,
    selectedSegment?.metadata,
    selectedSegment?.segment_id,
  ]);

  // Clear content when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Clear all content when modal closes to prevent showing stale data
      setSegments([]);
      setSelectedSegment(null);
      setError(null);
      setLoadErrorKind(null);
      loadErrorKindRef.current = null;
      setAvailableRuns([]);
      setSelectedRunId(null);
      availableRunsRef.current = [];
      selectedRunIdRef.current = null;
      setShowRunDropdown(false);
      setPromptRunsLoadedForVideoId(null);
      setLastLoadedRunId(null);
      setLastLoadedSegmentScopeKey(null);
      loadingSegmentScopeKeyRef.current = null;
      promptRunsRequestIdRef.current += 1;
      segmentsRequestIdRef.current += 1;
      cancelVideoLevelRequest();
      initializedVideoIdRef.current = null;
      setVideoLevelResult(null);
      setIsLoadingVideoLevelResult(false);
      setVideoLevelResultError(null);
      // Clear segmented player states
      setGlobalTime(0);
      setIsPlaying(false);
      appliedWorkspaceScopeRef.current = null;
      if (!workspaceState) setIsMuted(false);
      shouldAutoplaySegmentPlayerRef.current = false;
      // Reset metadata search
      if (!workspaceState) setMetadataSearchTerm('');
    }
  }, [cancelVideoLevelRequest, isOpen, workspaceState]);

  // Load segments when modal opens with a video
  useEffect(() => {
    if (!isOpen || !videoId) return;
    if (videoId === initializedVideoIdRef.current) return;

    // One initialization owner per video-open. The dedicated deep-link effects
    // handle run/time selection without reloading the run catalogue.
    initializedVideoIdRef.current = videoId;
    setSegments([]);
    setSelectedSegment(null);
    setError(null);
    setLiveVideoState(undefined);
    snapshotResultVersionsRef.current.clear();
    setLoadErrorKind(null);
    loadErrorKindRef.current = null;
    setAvailableRuns([]);
    setSelectedRunId(deepLinkRunId || null);
    availableRunsRef.current = [];
    selectedRunIdRef.current = deepLinkRunId || null;
    setShowRunDropdown(false);
    setPromptRunsLoadedForVideoId(null);
    setLastLoadedRunId(null);
    setLastLoadedSegmentScopeKey(null);
    loadingSegmentScopeKeyRef.current = null;
    promptRunsRequestIdRef.current += 1;
    segmentsRequestIdRef.current += 1;
    cancelVideoLevelRequest();
    setVideoLevelResult(null);
    setIsLoadingVideoLevelResult(false);
    setVideoLevelResultError(null);
    setGlobalTime(0);
    setIsPlaying(false);
    // Embedded Studio restoration starts paused. The saved time can be
    // restored exactly, while implicit autoplay after a route remount is not
    // a reliable or truthful reconstruction of the user's playback intent.
    shouldAutoplaySegmentPlayerRef.current = (
      presentation !== 'embedded'
      && !videoIsImage
      && isPlaybackVisibleRef.current
    );
    setIsLoading(true);

    if (runCatalog === undefined) void refreshPromptRuns().catch(() => undefined);
  }, [cancelVideoLevelRequest, deepLinkRunId, isOpen, presentation, refreshPromptRuns, runCatalog, videoId, videoIsImage]);

  useEffect(() => {
    if (!isOpen || !videoId || runCatalog === undefined) return;
    const runs = runCatalog.filter((run) => run.run_context?.video_ids?.includes(videoId));
    availableRunsRef.current = runs;
    setAvailableRuns(runs);
    const nextRunId = runScopeId === undefined ? selectVideoRun(videoId, runs)?.run_id || null : runScopeId;
    selectedRunIdRef.current = nextRunId;
    setSelectedRunId(nextRunId);
    setPromptRunsLoadedForVideoId(videoId);
    setIsLoadingRuns(false);
  }, [isOpen, runCatalog, runScopeId, videoId]);

  // Reconcile run status after visibility/tab changes so stale "processing" state is corrected.
  useEffect(() => {
    const becameVisible = canObserve && !wasPageVisibleRef.current;
    wasPageVisibleRef.current = canObserve;
    if (!becameVisible) return;
    if (!isOpen || !videoId || !canObserve || runCatalog !== undefined) return;
    if (promptRunsLoadedForVideoId !== videoId) return;
    void refreshPromptRuns(true).catch(() => undefined);
  }, [isOpen, videoId, canObserve, promptRunsLoadedForVideoId, refreshPromptRuns, runCatalog]);

  const selectedRunVideoLevelEnabled = Boolean(selectedRun?.video_level_enabled);
  const statusVideo = video && liveVideoState?.video_id === video.video_id
    ? applyRealtimeStateToVideo(video, liveVideoState)
    : video;
  const displayedVideoStatus = getDisplayedVideoStatus(statusVideo, selectedRunId, selectedRun?.status);
  const currentProgress = getVideoProgressSummary(statusVideo, selectedRunId, selectedRun?.status, selectedRun?.liveness);
  const shouldSubscribeToProcessingStream =
    canObserve &&
    isOpen &&
    Boolean(video);

  // Subscribe to shared processing realtime stream for live segment/run updates.
  useEffect(() => {
    if (!shouldSubscribeToProcessingStream || !videoId) return;

    const unsubscribe = processingRealtimeStore.subscribe((event) => {
      const payload = event.payload || {};
      const activeRunId = selectedRunIdRef.current;
      const eventRunId = String(event.run_id || payload.run_id || '').trim() || null;
      const eventVideoIds = [event.video_id, payload.video_id].filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
      );
      // A shared run does not make a sibling part's media events ours.
      if (eventVideoIds.some((id) => id !== videoId)) {
        return;
      }
      const eventTargetsCurrentVideo = (() => {
        if (eventVideoIds.length > 0) {
          return true;
        }
        if (!eventRunId) {
          return false;
        }
        if (activeRunId && eventRunId === activeRunId) {
          return true;
        }
        const targetVideoIds = getProcessingEventScopedVideoIds(event);
        if (targetVideoIds.includes(videoId)) {
          return true;
        }
        return availableRunsRef.current.some(run => run.run_id === eventRunId);
      })();

      if (!eventTargetsCurrentVideo) {
        return;
      }

      if (event.event_type === 'video.progress') {
        const state = processingRealtimeStore.getVideoState(videoId);
        setLiveVideoState(state);
        const projection = state?.processing_status?.find((run) => run.run_id === activeRunId);
        if (projection && activeRunId) {
          const revisions = projection.result_revisions;
          const segmentScope = `${videoId}:${activeRunId}:segments`;
          const segmentVersion = resultRevisionKey(revisions, ['metadata', 'segments', 'assets', 'image_index']);
          const previousSegments = snapshotResultVersionsRef.current.get(segmentScope);
          snapshotResultVersionsRef.current.set(segmentScope, segmentVersion);
          if (previousSegments !== segmentVersion && (projection.total_segments > 0 || projection.readiness?.metadata_available)) {
            void refreshSegments(true);
          }
          const videoLevelScope = `${videoId}:${activeRunId}:video-level`;
          const videoLevelVersion = resultRevisionKey(revisions, ['video_level']);
          const previousVideoLevel = snapshotResultVersionsRef.current.get(videoLevelScope);
          snapshotResultVersionsRef.current.set(videoLevelScope, videoLevelVersion);
          if (previousVideoLevel !== videoLevelVersion && revisions?.video_level) {
            void refreshVideoLevelResult(activeRunId, { silent: true, force: true });
          }
        }
      }

      if (eventRunId && event.event_type === 'run.progress') {
        setAvailableRuns((currentRuns) => {
          const existingIndex = currentRuns.findIndex((run) => run.run_id === eventRunId);
          const existingRun = existingIndex >= 0 ? currentRuns[existingIndex] : undefined;
          const patchedRun = toPromptRunFromProcessingEvent(event, existingRun);
          if (!patchedRun) return currentRuns;

          const nextRuns = existingIndex >= 0
            ? currentRuns.map((run, index) => index === existingIndex ? patchedRun : run)
            : [patchedRun, ...currentRuns];
          availableRunsRef.current = nextRuns;
          return nextRuns;
        });
      }

    }, { video_ids: [videoId], ...(selectedRunId ? { run_ids: [selectedRunId] } : {}) });

    return unsubscribe;
  }, [refreshSegments, refreshVideoLevelResult, selectedRunId, shouldSubscribeToProcessingStream, videoId]);

  // Handle ESC key to close sidebar
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen && isPresentationVisible) {
      document.addEventListener('keydown', handleEscKey);
      // Don't prevent background scroll for sidebar
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, isPresentationVisible, onClose]);

  // Load/reload segments only after prompt runs have resolved for the current video.
  useEffect(() => {
    if (!isOpen || !videoId) {
      return;
    }
    if (promptRunsLoadedForVideoId !== videoId) {
      return;
    }

    const nextScopeKey = `${videoId}:${selectedRunId || 'no-run'}:${visibleSegmentScopeKey}`;
    if (lastLoadedSegmentScopeKey === nextScopeKey) {
      return;
    }
    if (loadingSegmentScopeKeyRef.current === nextScopeKey) {
      return;
    }
    loadingSegmentScopeKeyRef.current = nextScopeKey;

    setSegments([]);
    setSelectedSegment(null);
    if (playbackMode !== 'continuous-video' && (!isEmbeddedPresentation || lastLoadedRunId !== selectedRunId)) setGlobalTime(0);
    setIsLoading(true);
    void refreshSegments(false).finally(() => {
      if (loadingSegmentScopeKeyRef.current === nextScopeKey) {
        loadingSegmentScopeKeyRef.current = null;
      }
    });
  }, [
    isOpen,
    isEmbeddedPresentation,
    playbackMode,
    lastLoadedRunId,
    lastLoadedSegmentScopeKey,
    promptRunsLoadedForVideoId,
    refreshSegments,
    selectedRunId,
    visibleSegmentScopeKey,
    videoId,
  ]);


  const loadVideoLevelResult = useCallback(async (
    runId: string,
    options: { silent?: boolean; force?: boolean } = {}
  ) => {
    if (!videoId) {
      cancelVideoLevelRequest();
      setVideoLevelResult(null);
      setVideoLevelResultError(null);
      setIsLoadingVideoLevelResult(false);
      return;
    }

    const selectedRun = availableRunsRef.current.find((run) => run.run_id === runId) || null;
    if (!selectedRun?.video_level_enabled || !showVideoLevelOutput) {
      cancelVideoLevelRequest();
      setVideoLevelResult(null);
      setVideoLevelResultError(null);
      setIsLoadingVideoLevelResult(false);
      return;
    }

    const requestedVideoId = videoId;
    const scopeKey = `${requestedVideoId}:${runId}`;
    const existingRequest = videoLevelRequestRef.current;
    if (existingRequest?.scopeKey === scopeKey && !options.force) {
      return existingRequest.promise;
    }
    if (existingRequest) {
      cancelVideoLevelRequest();
    }

    const requestId = videoLevelRequestIdRef.current + 1;
    videoLevelRequestIdRef.current = requestId;
    const controller = new AbortController();
    const isStaleRequest = () => (
      videoLevelRequestIdRef.current !== requestId
      || selectedRunIdRef.current !== runId
      || selectedVideoIdRef.current !== requestedVideoId
      || !isOpenRef.current
    );

    if (!options.silent) {
      setIsLoadingVideoLevelResult(true);
    }

    const request = (async () => {
      try {
        const result = await apiService.getPromptRunVideoResult(
          runId,
          requestedVideoId,
          controller.signal,
        );
        if (isStaleRequest()) return;
        setVideoLevelResult(result);
        setVideoLevelResultError(null);
      } catch (videoLevelError: any) {
        if (
          controller.signal.aborted
          || videoLevelError?.name === 'AbortError'
          || videoLevelError?.code === 'ERR_CANCELED'
          || isStaleRequest()
        ) return;
        if (videoLevelError?.response?.status === 404) {
          setVideoLevelResult(null);
          setVideoLevelResultError(null);
        } else {
          setVideoLevelResult(null);
          setVideoLevelResultError(
            videoLevelError?.response?.data?.detail || 'Failed to load video-level output'
          );
        }
      } finally {
        if (videoLevelRequestRef.current?.controller === controller) {
          videoLevelRequestRef.current = null;
        }
        if (!isStaleRequest()) {
          setIsLoadingVideoLevelResult(false);
        }
      }
    })();
    videoLevelRequestRef.current = { scopeKey, controller, promise: request };
    return request;
  }, [cancelVideoLevelRequest, showVideoLevelOutput, videoId]);

  useEffect(() => {
    if (!isOpen || !videoId || !selectedRunId) {
      cancelVideoLevelRequest();
      setVideoLevelResult(null);
      setVideoLevelResultError(null);
      setIsLoadingVideoLevelResult(false);
      return;
    }

    if (!selectedRunVideoLevelEnabled) {
      cancelVideoLevelRequest();
      setVideoLevelResult(null);
      setVideoLevelResultError(null);
      setIsLoadingVideoLevelResult(false);
      return;
    }

    void loadVideoLevelResult(selectedRunId);
  }, [cancelVideoLevelRequest, isOpen, loadVideoLevelResult, selectedRunId, selectedRunVideoLevelEnabled, videoId]);

  // Save width to localStorage when it changes
  useEffect(() => {
    if (presentation !== 'sidebar' || !isActive) return;
    localStorage.setItem('videoSidebarWidth', sidebarWidth.toString());
    // Dispatch event to update IndexTab margin
    window.dispatchEvent(new CustomEvent('sidebarWidthChange', { detail: { width: sidebarWidth } }));
  }, [isActive, presentation, sidebarWidth]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    if (presentation !== 'sidebar') return;
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  useEffect(() => {
    if (presentation !== 'sidebar' || !isPresentationVisible) return undefined;

    if (isResizing) {
      document.body.classList.add('resizing-sidebar');
    } else {
      document.body.classList.remove('resizing-sidebar');
    }

    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX; // Reversed because we're dragging from the left
      const newWidth = Math.max(400, Math.min(1400, resizeStartWidth.current + deltaX)); // Min 400px, max 1400px
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('resizing-sidebar');
    };
  }, [isResizing, presentation, isPresentationVisible]);

  const loadPromptRuns = async (forceRefresh = false): Promise<PromptRun[]> => {
    if (!video) return [];
    if (runCatalog !== undefined) return [...runCatalog];
    const requestedVideo = video;
    const requestedVideoId = requestedVideo.video_id;
    const requestId = ++promptRunsRequestIdRef.current;
    const isStaleRequest = () => (
      requestId !== promptRunsRequestIdRef.current
      || selectedVideoIdRef.current !== requestedVideoId
      || !isOpenRef.current
    );

    // Don't show loading state if we already have runs (to prevent UI flicker)
    if (!availableRuns.length) {
      setIsLoadingRuns(true);
    }

    try {
      const runs = await segmentCache.loadPromptRuns(requestedVideoId, forceRefresh);

      if (isStaleRequest()) {
        return [];
      }

      const loadedRuns = runs || [];

      if (isStaleRequest()) {
        return [];
      }

      const currentSelectedRunId = selectedRunIdRef.current;
      const retainedSelectedRun = currentSelectedRunId
        ? availableRunsRef.current.find((run) => run.run_id === currentSelectedRunId) || null
        : null;
      const nextAvailableRuns = (
        retainedSelectedRun
        && !loadedRuns.some((run) => run.run_id === retainedSelectedRun.run_id)
      )
        ? [retainedSelectedRun, ...loadedRuns]
        : loadedRuns;

      if (nextAvailableRuns.length > 0) {
        setAvailableRuns(nextAvailableRuns);
        availableRunsRef.current = nextAvailableRuns;

        // The run catalogue is status metadata, not selection authority.
        // Realtime/visibility refreshes may be temporarily incomplete, so
        // they must never replace an active run and reset media playback.
        if (!currentSelectedRunId) {
          const nextRunId = selectVideoRun(requestedVideoId, nextAvailableRuns)?.run_id || nextAvailableRuns[0].run_id;
          setLastLoadedSegmentScopeKey(null);
          selectedRunIdRef.current = nextRunId;
          setSelectedRunId(nextRunId);
          onRunSelectionChange?.(nextRunId);
        }
      } else {
        // An empty refresh is not proof that a previously selected run was
        // removed. Keep its scope and playback intact; only initialize the
        // no-run view when there was no active selection to begin with.
        setAvailableRuns([]);
        availableRunsRef.current = [];
        if (!selectedRunIdRef.current) {
          setSelectedRunId(null);
          setLastLoadedRunId(null);
          setLastLoadedSegmentScopeKey(null);
        }
      }

      if (loadErrorKindRef.current !== 'segments') {
        setError(null);
        setLoadErrorKind(null);
        loadErrorKindRef.current = null;
      }
      setPromptRunsLoadedForVideoId(requestedVideoId);
      return loadedRuns;
    } catch (err) {
      if (isStaleRequest()) {
        return [];
      }
      console.error('Failed to load prompt runs:', err);

      setLoadErrorKind('runs');
      loadErrorKindRef.current = 'runs';
      setError('Failed to load extraction runs');
      throw err;
    } finally {
      if (!isStaleRequest()) {
        setIsLoadingRuns(false);
      }
    }
  };

  const loadSegments = async (forceRefresh = false) => {
    if (!video) return;
    const requestedVideo = video;
    const requestedVideoId = requestedVideo.video_id;
    const runIdForRequest = selectedRunId;
    const visibleSegmentScopeKeyForRequest = visibleSegmentScopeKey;
    const visibleSegmentIdsForRequest = normalizedVisibleSegmentIds;
    const visibleSegmentIdSetForRequest = visibleSegmentIdsForRequest === null
      ? null
      : new Set(visibleSegmentIdsForRequest);
    const segmentScopeKey = `${requestedVideoId}:${runIdForRequest || 'no-run'}:${visibleSegmentScopeKeyForRequest}`;
    const requestId = ++segmentsRequestIdRef.current;
    const isStaleRequest = () => (
      requestId !== segmentsRequestIdRef.current
      || selectedVideoIdRef.current !== requestedVideoId
      || selectedRunIdRef.current !== runIdForRequest
      || visibleSegmentScopeKeyRef.current !== visibleSegmentScopeKeyForRequest
      || !isOpenRef.current
    );

    if (segments.length === 0) {
      setIsLoading(true);
    }

    try {
      const segmentData = visibleSegmentIdsForRequest?.length === 0 || (runCatalog !== undefined && !runIdForRequest)
        ? []
        : await segmentCache.loadSegments(requestedVideoId, runIdForRequest, forceRefresh);

      if (isStaleRequest()) {
        return;
      }

      const scopedSegmentData = visibleSegmentIdSetForRequest === null
        ? segmentData
        : segmentData.filter((segment: any) => (
            visibleSegmentIdSetForRequest.has(String(segment.segment_id || '').trim())
          ));

      // Convert API response to SegmentData format
      const processedSegments: SegmentData[] = scopedSegmentData.map((segment: any) => ({
        segment_status: segment.segment_status,
        failure_stage: segment.failure_stage,
        failure_message: segment.failure_message,
        attempt_id: segment.attempt_id,
        status_source: segment.status_source,
        segment_id: segment.segment_id,
        start_time: segment.start_time,
        end_time: segment.end_time,
        processed: segment.segment_status
          ? segment.segment_status === 'successful'
          : segment.processed,
        processing_failed: segment.segment_status
          ? segment.segment_status === 'failed'
          : segment.processing_failed,
        metadata: segment.metadata,
        metadata_available: segment.metadata_available,
        metadata_text: segment.metadata_text,
        processing_warning: segment.processing_warning,
        error_message: segment.failure_message || segment.error_message,
        segment_uri: segment.segment_uri || segment.gcs_uri,
        gcs_uri: segment.gcs_uri,
        playback_source_uri: segment.playback_source_uri,
        playback_start_time: segment.playback_start_time,
        playback_end_time: segment.playback_end_time,
        thumbnail_gcs_uri: segment.thumbnail_gcs_uri,
        gif_gcs_uri: segment.gif_gcs_uri,
        thumbnail_uri: segment.thumbnail_uri,
        gif_uri: segment.gif_uri,
        thumbnail_available: segment.thumbnail_available || false,
        gif_available: segment.gif_available || false,
        // Operation status flags
        field_extraction_succeeded: segment.field_extraction_succeeded,
        image_embedding_status: segment.image_embedding_status,
        field_extraction_error: segment.field_extraction_error,
        image_embedding_error: segment.image_embedding_error,
        marker: segment.marker,
        metadata_markers: segment.metadata_markers,
      }));

      // Set segments and prepare for player
      setSegments(processedSegments);
      setLastLoadedRunId(runIdForRequest || null);
      setLastLoadedSegmentScopeKey(segmentScopeKey);
      const preparedSegments = prepareSegments(processedSegments, requestedVideoId);
      // Clear error on successful load
      if (loadErrorKindRef.current !== 'runs') {
        setError(null);
        setLoadErrorKind(null);
        loadErrorKindRef.current = null;
      }

      // Select the initial extraction annotation without changing media
      // authority; playback remains on the continuous full-video source.
      if (processedSegments.length > 0) {
        // Select the segment with actual run data
        // For a run, prefer segments that are processed or have metadata
        const sortedSegments = [...processedSegments].sort((a, b) => a.start_time - b.start_time);

        // Find the segment with run data (processed=true or has metadata)
        const segmentWithData = sortedSegments.find(s =>
          resolveSegmentProcessingStatus(s) === 'successful' || (s.metadata && Object.keys(s.metadata).length > 0)
        );
        const restoredSegment = workspaceState?.selectedSegmentId
          ? sortedSegments.find(
              (segment) => segment.segment_id === workspaceState.selectedSegmentId,
            )
          : null;
        // Resolve the persisted playback target before exposing the initial
        // annotation so the player performs one initial seek only.
        const pendingInitialTime = (
          !deepLinkTimeAppliedRef.current
          && deepLinkTime !== null
          && deepLinkTime !== undefined
          && Number.isFinite(deepLinkTime)
        ) ? Math.max(0, deepLinkTime) : null;
        const linkedVideoSegment = pendingInitialTime !== null && preparedSegments.length > 0
          ? locateGlobalTime(pendingInitialTime, preparedSegments).segment
          : null;
        const linkedSegment = linkedVideoSegment
          ? sortedSegments.find((segment) => segment.segment_id === linkedVideoSegment.id) || null
          : null;
        const exactSegmentId = resultLink?.videoId === requestedVideoId && (!resultLink.runId || resultLink.runId === runIdForRequest) ? resultLink.segmentId : null;
        const selectedSeg = exactSegmentId
          ? sortedSegments.find((segment) => segment.segment_id === exactSegmentId)
          : linkedSegment || restoredSegment || segmentWithData || sortedSegments[0];

        if (!selectedSeg && exactSegmentId) setSelectedSegment(null);
        if (selectedSeg) {
          setSelectedSegment(selectedSeg);
          updateWorkspaceState({ selectedSegmentId: selectedSeg.segment_id });
          const selectedVideoSegment = preparedSegments.find(segment => segment.id === selectedSeg.segment_id);
          if (selectedVideoSegment) {
            const initialGlobalTime = exactSegmentId ? selectedVideoSegment.start : pendingInitialTime ?? selectedVideoSegment.start;
            if (exactSegmentId) explicitSegmentTimeRef.current = initialGlobalTime;
            if (initialGlobalTime === 0) {
              allowZeroTimeSelectionRef.current = true;
            }
            // The parent persists time updates emitted by this player. Consume
            // the one-time restore boundary even when the initial value is
            // zero/null so our own first persistence write cannot be reapplied
            // as a new seek (or a source switch at a segment boundary).
            const preservePlaybackTime = playbackMode === 'continuous-video'
              ? deepLinkTimeAppliedRef.current && !exactSegmentId
              : (isEmbeddedPresentation || forceRefresh)
                && deepLinkTimeAppliedRef.current && lastLoadedRunId === runIdForRequest;
            deepLinkTimeAppliedRef.current = true;
            if (!preservePlaybackTime) setGlobalTime(initialGlobalTime);
            if (
              shouldAutoplaySegmentPlayerRef.current
              && isPlaybackVisibleRef.current
              && !isImageVideo(requestedVideo)
            ) {
              setIsPlaying(true);
              shouldAutoplaySegmentPlayerRef.current = false;
            }
          }
        }
      } else {
        // No segments available yet (this is OK - video might still be processing)
        setSelectedSegment(null);
      }

      setIsLoading(false);
    } catch (err: any) {
      if (isStaleRequest()) {
        return;
      }
      console.error('Failed to load segments:', err);

      setLoadErrorKind('segments');
      loadErrorKindRef.current = 'segments';
      setError(err.response?.data?.detail || 'Failed to load video segments');
      setIsLoading(false);
    }
  };

  // Keep effects on a stable invocation surface while always
  // dispatching to the loaders from the latest committed render.
  useLayoutEffect(() => {
    loadPromptRunsRef.current = loadPromptRuns;
    loadSegmentsRef.current = loadSegments;
    loadVideoLevelResultRef.current = loadVideoLevelResult;
  });

  const handleSegmentClick = (segment: SegmentData) => {
    setResultLinkError(null);
    changeResultLink({ segmentId: segment.segment_id, output: 'segment', time: null });
    setSelectedSegment(segment);
    updateWorkspaceState({ selectedSegmentId: segment.segment_id });

    // If we have the segmented player, jump to this segment's time
    if (videoSegments.length > 0) {
      // Find the corresponding segment in videoSegments
      const videoSegment = videoSegments.find(vs => vs.id === segment.segment_id);
      if (videoSegment) {
        // Jump to the start of this segment in global time
        setGlobalTime(videoSegment.start);
        onPlaybackTimeChange?.(videoSegment.start);
        setIsPlaying(true); // Start playing when segment is clicked
      }
    }

  };

  const handleRetryLoad = () => {
    const failedLoad = loadErrorKind;
    setError(null);
    setLoadErrorKind(null);
    loadErrorKindRef.current = null;
    if (failedLoad === 'runs') {
      void refreshPromptRuns(true).catch(() => undefined);
      return;
    }
    if (failedLoad === 'segments') {
      void refreshSegments(true);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle sort direction
  const handleSortToggle = () => {
    const nextDirection = metadataSortDir === 'asc' ? 'desc' : 'asc';
    setMetadataSortDir(nextDirection);
    updateWorkspaceState({ metadataSortDirection: nextDirection });
  };

  // Toggle all fields expanded/collapsed
  const handleToggleAllFields = () => {
    if (allFieldsExpanded) {
      const nextPaths = new Set(getCollapsibleMetadataPaths(selectedSegment?.metadata));
      setCollapsedMetadataPaths(nextPaths);
      updateWorkspaceState({ collapsedMetadataPaths: Array.from(nextPaths) });
    } else {
      setCollapsedMetadataPaths(new Set());
      updateWorkspaceState({ collapsedMetadataPaths: [] });
    }
  };

  // Toggle nested path expansion
  const toggleMetadataPath = useCallback((path: string) => {
    const next = new Set(collapsedMetadataPaths);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setCollapsedMetadataPaths(next);
    updateWorkspaceState({ collapsedMetadataPaths: Array.from(next) });
  }, [collapsedMetadataPaths, updateWorkspaceState]);

  // Toggle video level nested path expansion
  const toggleVideoLevelMetadataPath = useCallback((path: string) => {
    const next = new Set(collapsedVideoLevelPaths);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setCollapsedVideoLevelPaths(next);
    updateWorkspaceState({ collapsedVideoLevelPaths: Array.from(next) });
  }, [updateWorkspaceState, collapsedVideoLevelPaths]);

  const handleDownloadVideoMetadata = useCallback(async () => {
    if (!video) return;

    const exportRuns = selectedRunId
      ? availableRuns.filter((run) => run.run_id === selectedRunId)
      : availableRuns;
    const promptRunsData = await Promise.all(
      exportRuns.map(async (run) => {
        const allRunSegments = await segmentCache.loadSegments(video.video_id, run.run_id);
        const runSegments = exportSegmentIdSet === null
          ? allRunSegments
          : allRunSegments.filter((segment: SegmentData) => exportSegmentIdSet.has(segment.segment_id));
        let videoLevelOutput: VideoPromptRunResult | null = null;
        if (run.video_level_enabled && exportVideoLevelOutput) {
          try {
            videoLevelOutput = await apiService.getPromptRunVideoResult(run.run_id, video.video_id);
          } catch (videoLevelError: any) {
            if (videoLevelError?.response?.status !== 404) {
              console.warn(`Failed to load export video-level output for run ${run.run_id}:`, videoLevelError);
            }
          }
        }

        return buildStudioRunMetadata({
          run, segments: runSegments, videoLevelOutput, mediaType: video.media_type,
        });
      })
    );

    // Build the export data
    const exportData = buildStudioMetadataExport({
      video,
      promptRuns: promptRunsData,
      resultScope: visibleSegmentIdSet === null ? 'all' : 'matches',
      ...(isStudio ? { resultSearch: initialWorkspaceState.resultSearchTerm, resultFilter: initialWorkspaceState.resultFilter } : {}),
    });

    const cleanTitle = sanitizeDownloadFilenamePart(video.title, 'video', 30);
    const filename = `${cleanTitle}_${visibleSegmentIdSet === null ? 'all' : 'matches'}_metadata.json`;
    downloadJsonFile(exportData, filename);
  }, [video, availableRuns, selectedRunId, exportVideoLevelOutput, exportSegmentIdSet, visibleSegmentIdSet, isStudio, initialWorkspaceState.resultSearchTerm, initialWorkspaceState.resultFilter]);

  const currentRun = selectedRun;
  const shouldShowRunSelector = runCatalog === undefined && !searchContext && availableRuns.length > 0 && visibleSegmentIds == null;
  const inferFailedOperationFromStage = useCallback((stage?: string | null): string => {
    const normalized = String(stage || '').toLowerCase();
    if ((normalized.includes('image') || normalized.includes('embedding') || normalized.includes('vision')) && currentRun?.enable_image_embedding !== false) {
      return 'image_embedding';
    }
    if (normalized.includes('field') || normalized.includes('extract') || normalized.includes('schema')) {
      return 'field_extraction';
    }
    return 'processing';
  }, [currentRun?.enable_image_embedding]);

  const getSegmentFailedOperations = useCallback((segment: SegmentData): string[] => {
    const failedOps: string[] = [];
    if (segment.field_extraction_succeeded === false) {
      failedOps.push('field_extraction');
    }
    if (currentRun?.enable_image_embedding !== false && segment.image_embedding_status === 'failed') {
      failedOps.push('image_embedding');
    }
    if (
      failedOps.length === 0
      && resolveSegmentProcessingStatus(segment) === 'failed'
      && segment.failure_stage
    ) {
      failedOps.push(inferFailedOperationFromStage(segment.failure_stage));
    }
    return failedOps;
  }, [currentRun?.enable_image_embedding, inferFailedOperationFromStage]);

  const runProjection = getRunScopedVideoProjection(statusVideo, selectedRunId);
  const selectedRunActivity = !isInProgressStatus(currentProgress.status) ? null
    : ['pending', 'queued'].includes(currentProgress.phase || currentProgress.status) ? 'queued' : 'processing';
  const videoLevelProjection = runProjection?.video_level || null;
  const selectedRunHasVideoLevel = showVideoLevelOutput && Boolean(currentRun?.video_level_enabled);
  const canDownloadVideoMetadata = !studioResultsUnavailable
    && currentSegmentScopeKey === lastLoadedSegmentScopeKey
    && (displayedSegments.length > 0 || (exportVideoLevelOutput && selectedRunHasVideoLevel && videoLevelResult?.status === 'successful'));
  const videoLevelDisplayStatus = videoLevelResult?.status || videoLevelProjection?.status || 'pending';
  const formatVideoLevelStatusLabel = useCallback((status?: string | null): string => {
    const normalized = String(status || '').trim().toLowerCase();
    if (!normalized) return 'pending';
    if (normalized === 'completed_with_failures') return 'completed with failures';
    return normalized.replace(/_/g, ' ');
  }, []);

  const segmentationInfo = formatRunSegmentationLabel(currentRun, video?.media_type);

  const runExecutedAtLabel = currentRun?.executed_at
    ? new Date(currentRun.executed_at).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : null;

  const shouldSplitStudioWorkspace = (
    isEmbeddedPresentation
    && !videoIsImage
    && (isStudio || selectedSegment !== null || isLoading || segments.length > 0)
  );
  const studioHasNoMatches = isStudio && !isLoading && !studioResultsUnavailable
    && currentSegmentScopeKey === lastLoadedSegmentScopeKey && displayedSegments.length === 0
    && (studioShowsMatches || Boolean(initialWorkspaceState.resultSearchTerm.trim() || initialWorkspaceState.resultFilter));
  const emptySegmentResults = isLoading ? <ResultPlaceholder title="Loading segments…" busy />
    : studioResultsUnavailable ? null
    : selectedRunActivity ? (
      <ResultPlaceholder title={selectedRunActivity === 'processing' ? 'Your job is processing' : 'Your job is queued'} busy>
        {selectedRunActivity === 'processing' ? 'Segments will appear here as they become available.' : 'Processing will begin shortly.'}
      </ResultPlaceholder>
    ) : (
      <ResultPlaceholder title={studioHasNoMatches ? 'No matching segments' : 'No segments available'}>
        {studioHasNoMatches
          ? localResultSelection?.videoMatched
            ? 'The whole video matches. Its output is below; no individual segments match.'
            : 'Change or clear your search and filters to see more extracted results.'
          : 'Extracted segments will appear here.'}
      </ResultPlaceholder>
    );

  if (!video) return null;

  return (
    <>
      <div
        ref={modalRef}
        className={`video-detail-modal video-detail-modal--${presentation} ${isOpen ? 'sidebar-open' : ''} ${presentation === 'sidebar' && isResizing ? 'resizing' : ''} ${!isPresentationVisible ? 'video-detail-modal--hidden' : ''}`}
        style={presentation === 'sidebar' ? { width: `${sidebarWidth}px` } : undefined}
        hidden={!isPresentationVisible}
        aria-hidden={!isPresentationVisible || undefined}
      >
        {resultLinkError && <div className="error-message" role="alert">{resultLinkError}</div>}
        {/* Resize handle */}
        {presentation === 'sidebar' && (
          <div
            className={`resize-handle ${isResizing ? 'resizing' : ''}`}
            onMouseDown={handleResizeStart}
          />
        )}

        {!isEmbeddedPresentation && (
          <div className="modal-header">
            <div className="modal-title">
              <h2>{video.title}</h2>
              <div className="status-info">
                <span className="video-status">Status: {displayedVideoStatus}</span>
              </div>
            </div>
            <div className="modal-header-actions">
              <FeatureAction feature="export_lifecycle"><button
                className="export-video-btn"
                onClick={handleDownloadVideoMetadata}
                title="Export the current result view as JSON"
                disabled={!canDownloadVideoMetadata}
              >
                <Download size={18} />
                Export Metadata
              </button></FeatureAction>
              <button
                type="button"
                className="close-button"
                onClick={onClose}
                aria-label="Close video details"
              >
                <X size={24} />
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
            {loadErrorKind && (
              <button type="button" onClick={handleRetryLoad}>Retry</button>
            )}
            <button onClick={() => { setError(null); setLoadErrorKind(null); loadErrorKindRef.current = null; }}>×</button>
          </div>
        )}

        <div className="modal-content" key={video.video_id}>
          <div className={`video-detail-workspace ${shouldSplitStudioWorkspace ? 'video-detail-workspace--split' : ''} ${shouldShowRunSelector || searchContext ? 'video-detail-workspace--with-run-selector' : ''} ${searchContext ? 'video-detail-workspace--search' : ''}`}>
          {searchContext && <div className="run-selector-section studio-search-player-navigation">{searchContext.navigation}</div>}
          {/* Extraction Run Selector */}
          {shouldShowRunSelector && (
            <div className="run-selector-section">
              <div className="run-selector-row">
                <div className="custom-select-container" ref={runDropdownRef}>
                  <button
                    type="button"
                    className="custom-select-trigger"
                    aria-expanded={showRunDropdown}
                    aria-controls={showRunDropdown ? runDropdownId : undefined}
                    title="Choose extraction run"
                    disabled={availableRuns.length === 0}
                    onClick={() => {
                      if (availableRuns.length > 0) {
                        setShowRunDropdown(!showRunDropdown);
                      }
                    }}
                  >
                    <span className={`select-value ${currentRun ? 'selected-run-value' : ''}`}
                      title={currentRun ? [currentRun.prompt_name, segmentationInfo, runExecutedAtLabel].filter(Boolean).join(' · ') : undefined}>
                      {currentRun ? (
                        <>
                          <span className="run-config-item">
                            <span className="config-value">{currentRun.prompt_name}</span>
                          </span>
                          {segmentationInfo && (
                            <span className="run-config-item">
                              <span className="config-value">{segmentationInfo}</span>
                            </span>
                          )}
                          {runExecutedAtLabel && (
                            <span className="run-config-item">
                              <span className="config-value">{runExecutedAtLabel}</span>
                            </span>
                          )}
                          {(currentRun.total_videos || 0) > 0 && (
                            <span className="run-config-item">
                              <span className="config-value">{currentRun.total_videos} videos</span>
                            </span>
                          )}
                          {(currentRun.total_audios || 0) > 0 && (
                            <span className="run-config-item">
                              <span className="config-value">{currentRun.total_audios} audio files</span>
                            </span>
                          )}
                          {(currentRun.total_images || 0) > 0 && (
                            <span className="run-config-item">
                              <span className="config-value">{currentRun.total_images} images</span>
                            </span>
                          )}
                        </>
                      ) : 'Select an extraction run'}
                    </span>
                    <span className="run-selector-affordance">
                      <span>Change run</span>
                      {showRunDropdown ? <ChevronUp size={18} aria-hidden="true" /> : <ChevronDown size={18} aria-hidden="true" />}
                    </span>
                  </button>

                  {showRunDropdown && (
                    <div className="custom-dropdown" id={runDropdownId}>
                      <div className="dropdown-header">
                        <span className="dropdown-title">Select Extraction Run</span>
                      </div>
                      <div className="dropdown-list">
                        {availableRuns.map(run => {
                          const isSelected = selectedRunId === run.run_id;
                          const getStatusIcon = (status: boolean | null | undefined) => {
                            if (status === null || status === undefined) return <Clock size={12} className="status-pending" />;
                            if (status === true) return <CheckCircle size={12} className="status-success" />;
                            return <AlertCircle size={12} className="status-failed" />;
                          };
                          return (
                            <div
                              key={run.run_id}
                              className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                selectedRunIdRef.current = run.run_id;
                                setSelectedRunId(run.run_id);
                                onRunSelectionChange?.(run.run_id, { userInitiated: true });
                                setShowRunDropdown(false);
                              }}
                            >
                              <div className="dropdown-item-content">
                                <span className="field-name">{run.prompt_name || 'Extraction Run'}</span>
                                <div className="dropdown-status-icons">
                                  <span className="status-badge" title="Field Extraction">
                                    {getStatusIcon(run.field_extraction_succeeded)}
                                    <span className="status-label">Fields</span>
                                  </span>
                                </div>
                              </div>
                              <span className="run-date">
                                {new Date(run.executed_at).toLocaleDateString()}
                                {run.status === 'completed' ? ` • ${run.total_videos} videos` : ` • ${run.status}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          )}

          {/* Segmented Video Player - hidden for images */}
          {!videoIsImage && (isStudio || playerPlaybackSegments.length > 0 || isLoading || isInProgressStatus(currentProgress.status)) && (
            <div className="segmented-video-section">
              {isEmbeddedPresentation
                ? <div className="studio-player-filename" title={video.title}>{video.title}</div>
                : <h3>Video Player</h3>}
              <ProcessingProgressBar {...currentProgress} />
              <ImageEnrichmentStatus progress={runProjection?.enrichment?.image} />
              {isInProgressStatus(currentProgress.status) && <ProcessingConnectionNotice />}
              {studioHasNoMatches && playerPlaybackSegments.length > 0 && <p role="status">No segments match your filters or search.</p>}
              <div className="segmented-player-frame">
                {playerPlaybackSegments.length > 0 ? (
                  <SegmentedPlayer
                    segments={playerPlaybackSegments}
                    globalTime={globalTime}
                    onGlobalTimeChange={(time) => {
                      setGlobalTime(time);
                      deepLinkTimeAppliedRef.current = true;
                      onPlaybackTimeChange?.(time);
                    }}
                    playing={isPresentationVisible && isPlaying}
                    muted={isMuted}
                    volume={playbackVolume}
                    onPlayingChange={setIsPlaying}
                    onMutedChange={(muted) => {
                      setIsMuted(muted);
                      updateWorkspaceState({ muted });
                    }}
                    onVolumeChange={(volume) => {
                      setPlaybackVolume(volume);
                      updateWorkspaceState({ volume });
                    }}
                    onError={(err) => console.error('Player error:', err)}
                  />
                ) : studioHasNoMatches ? (
                  <div className="segmented-player-empty" role="status">
                    <Search size={32} aria-hidden="true" />
                    <p>No segments match your filters or search.</p>
                  </div>
                ) : isLoading || studioResultsUnavailable ? (
                  <div className="loading-state">
                    <RefreshCw size={20} className="spinning" />
                    <span>{currentStudioSelection?.error ? 'Matches are unavailable. Try again or turn off the engine filter.' : 'Loading video player...'}</span>
                  </div>
                ) : (
                  <div className="segmented-player-empty" role="status">
                    <p>No extracted segments are available to play.</p>
                  </div>
                )}
              </div>
              {(isStudio || playerPlaybackSegments.length > 0) && (
                  <SegmentedTimeline
                    segments={videoSegments}
                    globalTime={globalTime}
                    totalDuration={playbackTimelineDuration}
                    allowUnsegmentedSeeking={playbackMode === 'continuous-video'}
                    onChange={(newGlobalTime) => {
                      // Update global time
                      setGlobalTime(newGlobalTime);
                      onPlaybackTimeChange?.(newGlobalTime);
                      deepLinkTimeAppliedRef.current = true;
                      if (videoSegments.length === 0) return;

                      const playbackSegment = locateGlobalTime(newGlobalTime, videoSegments).segment;
                      const clickedSegment = segments.find(
                        (segment) => segment.segment_id === playbackSegment.id,
                      );

                      if (clickedSegment) {
                        if (resultLinkError || selectedSegment?.segment_id !== clickedSegment.segment_id) {
                          setResultLinkError(null);
                          changeResultLink({ segmentId: clickedSegment.segment_id, output: 'segment', time: null });
                        }
                        setSelectedSegment(clickedSegment);
                        updateWorkspaceState({ selectedSegmentId: clickedSegment.segment_id });
                      }
                    }}
                  />
              )}
            </div>
          )}

          {/* Image Preview - shown only for images */}
          {videoIsImage && (!isStudio || displayedSegments.length > 0) && (
            <div className="image-preview-section">
              <h3>Image</h3>
              <ProcessingProgressBar {...currentProgress} />
              <ImageEnrichmentStatus progress={runProjection?.enrichment?.image} />
              {isInProgressStatus(currentProgress.status) && <ProcessingConnectionNotice />}
              <div className="image-preview-container">
                {isLoadingFullImage ? (
                  <div className="loading-state">
                    <RefreshCw size={24} className="spinning" />
                    <span>Loading image...</span>
                  </div>
                ) : fullImageUrl ? (
                  <img
                    src={fullImageUrl}
                    alt={video.title}
                    className="image-preview"
                    onError={() => refreshFullImageAfterError(fullImageUrl)}
                  />
                ) : selectedThumbnailSource || selectedThumbnailProjection ? (
                  <GrantedPreviewImage
                    posterUrl={selectedThumbnailSource}
                    projection={selectedThumbnailProjection}
                    alt={video.title}
                    className="image-preview"
                    placeholder={(
                      <div className="no-segments">
                        <AlertCircle size={48} />
                        <p>Image not available</p>
                      </div>
                    )}
                  />
                ) : isLoading ? (
                  <div className="loading-state">
                    <RefreshCw size={24} className="spinning" />
                    <span>Loading...</span>
                  </div>
                ) : (
                  <div className="no-segments">
                    <AlertCircle size={48} />
                    <p>{studioShowsMatches ? 'No matching image' : 'Image not available'}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Studio result tools also apply to image metadata. */}
          {(!videoIsImage || isStudio) && (
            <div className={`timeline-with-gifs-section${isStudio ? ' studio-results-section' : ''}`}>
              {isStudio && selectedRun && !searchContext && (
                <StudioResultToolbar
                  key={studioSelectionKey}
                  state={initialWorkspaceState}
                  onChange={updateWorkspaceState}
                  segmentFields={studioSegmentFields}
                  videoFields={studioVideoFields}
                  runFilter={studioRunFilter}
                  hasRunFilter={studioHasFilter}
                  resultMode={studioShowsMatches ? 'matches' : 'all'}
                  onResultModeChange={(mode) => {
                    setStudioResultMode({ key: studioSelectionKey, mode });
                    changeResultLink({ resultScope: mode, segmentId: null, time: null });
                  }}
                  resultStatus={studioResultsUnavailable && (
                    <div className={`workflow-result-message${currentStudioSelection?.error ? ' workflow-result-error' : ''}`} role="status">
                      <span>{selectedRun.selection_summary?.status === 'failed'
                        ? 'The engine filter could not be applied. Turn it off to browse all extracted results.'
                        : !studioSelectionReady
                          ? 'Matches will be available after this execution finishes selecting results. Turn off the engine filter to browse all extracted results.'
                          : currentStudioSelection?.error || 'Loading matches…'}</span>
                      {currentStudioSelection?.error && (
                        <button type="button" onClick={() => setStudioSelection(null)}>Retry</button>
                      )}
                    </div>
                  )}
                />
              )}


            {(!studioResultsUnavailable || (isStudio && !videoIsImage)) && (videoIsImage && (segments.length > 0 || studioShowsMatches) && displayedSegments.length === 0 ? (
              <ResultPlaceholder title="No matching image results">
                Change or clear your search and filters to see more extracted results.
              </ResultPlaceholder>
            ) : videoIsImage ? null : segments.length > 0 || isStudio ? (
              <div className="timeline-container" key={`timeline-${selectedRunId}`}>
                <SegmentPreviewGallery ariaLabel="Video segment previews" stableHeight={isStudio} emptyState={emptySegmentResults}>
                  {(studioResultsUnavailable ? [] : displayedSegments).map((segment) => {
                    const segmentStatus = resolveSegmentProcessingStatus(segment);
                    const searchScore = getHighestFieldSearchScore(searchHitsBySegmentId.get(segment.segment_id));

                    return (
                      <SegmentPreviewGalleryItem
                        key={segment.segment_id}
                        selected={selectedSegment?.segment_id === segment.segment_id}
                        onSelect={() => handleSegmentClick(segment)}
                        title={`Play segment at ${formatTime(segment.start_time)}`}
                        timeLabel={`${formatTime(segment.start_time)} – ${formatTime(segment.end_time)}`}
                        status={segmentStatus}
                        actions={(
                          <SegmentDownloadActions
                            segment={segment}
                            segmentIndex={segments.indexOf(segment)}
                            videoId={video.video_id}
                            videoTitle={video.title}
                            runId={selectedRunId}
                            promptName={selectedRun?.prompt_name}
                            onError={(message) => {
                              setLoadErrorKind(null);
                              loadErrorKindRef.current = null;
                              setError(message);
                            }}
                          />
                        )}
                      >
                        <GrantedSegmentPreview
                          segment={segment}
                          videoId={video.video_id}
                          alt={`Segment ${segment.segment_id} GIF`}
                          fallback={
                          <div className="gif-placeholder-mini">
                            <div className="gif-status">
                              {segmentStatus === 'failed'
                                ? <AlertCircle size={12} />
                                : segmentStatus === 'successful'
                                  ? <CheckCircle size={12} />
                                  : segmentStatus === 'processing'
                                    ? <RefreshCw size={12} className="spinning" />
                                    : <Clock size={12} />
                              }
                            </div>
                          </div>
                          }
                        />
                        {searchScore !== null && <span className="search-segment-score" title={`Highest field score: ${formatSearchScore(searchScore)}`}>{formatSearchScore(searchScore)}</span>}
                      </SegmentPreviewGalleryItem>
                    );
                  })}
                </SegmentPreviewGallery>
              </div>
            ) : selectedRunActivity ? (
              <div className="no-segments run-lifecycle-state" aria-live="polite">
                {selectedRunActivity === 'processing'
                  ? <RefreshCw size={36} className="spinning" />
                  : <Clock size={36} />}
                <h3>{selectedRunActivity === 'processing' ? 'Your job is processing' : 'Your job is queued'}</h3>
                <p>{selectedRunActivity === 'processing'
                  ? 'Segments will appear here as they become available.'
                  : 'Processing will begin shortly.'}</p>
              </div>
            ) : !isLoading && segments.length === 0 ? (
              <div className="no-segments">
                <AlertCircle size={48} />
                <h3>{normalizedVisibleSegmentIds !== null ? 'No matching segments' : 'No segments available'}</h3>
                <p>{normalizedVisibleSegmentIds !== null
                  ? normalizedVisibleSegmentIds.length === 0
                    ? showVideoLevelOutput
                      ? 'This media item matched at the video level; no segments were selected.'
                      : 'This media item has no selected output in this workflow.'
                    : 'The selected workflow segments are unavailable.'
                  : "This video hasn't been processed yet or no segments were found."}</p>
              </div>
            ) : isLoading ? (
              <div className="loading-state">
                <RefreshCw size={24} className="spinning" />
                <span>Loading segments...</span>
              </div>
            ) : null)}
          </div>
          )}

          {/* Segment Metadata Display */}
          {!studioResultsUnavailable && <div className="segment-details-pane">
          {selectedSegment && selectedSegmentIsVisible ? (
            <div className="segment-details">
              <div className="segment-details-header">
                <h3>
                  {videoIsImage
                    ? 'Image Details'
                    : `Segment Details (${formatTime(selectedSegment.start_time)} - ${formatTime(selectedSegment.end_time)})`
                  }
                </h3>
                {/* Segment-level operation status */}
                {(() => {
                  const hasOpStatus = selectedSegment.field_extraction_succeeded !== undefined ||
                    (selectedSegment.image_embedding_status != null && selectedSegment.image_embedding_status !== 'not_applicable');

                  if (!hasOpStatus) return null;

                  return (
                    <div className="segment-operation-status">
                      <span className="segment-op-item" title={selectedSegment.field_extraction_error || "Field Extraction"}>
                        {getOperationStatusIcon(selectedSegment.field_extraction_succeeded, 12)}
                        <span className="op-label">Field Extraction</span>
                      </span>
                      {currentRun?.enable_image_embedding !== false && selectedSegment.image_embedding_status != null && selectedSegment.image_embedding_status !== 'not_applicable' && (
                        <span className="segment-op-item" title={selectedSegment.image_embedding_error || "Embedding"}>
                          {getOperationStatusIcon(selectedSegment.image_embedding_status === 'completed' ? true : selectedSegment.image_embedding_status === 'failed' ? false : null, 12)}
                          <span className="op-label">Embedding</span>
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>

              {(() => {
                const hasMetadata = selectedSegment.metadata && Object.keys(selectedSegment.metadata).length > 0;
                const selectedSegmentStatus = resolveSegmentProcessingStatus(selectedSegment);
                const selectedSegmentFailedOps = getSegmentFailedOperations(selectedSegment);
                const segmentHasFailureEvidence = (
                  selectedSegmentStatus === 'failed'
                  || selectedSegmentFailedOps.length > 0
                  || !!selectedSegment.failure_message
                  || !!selectedSegment.error_message
                );

                if (hasMetadata) {
                  return (
                    <div className="metadata-display">
                      <div className="metadata-display-header">
                        <div className="metadata-search-box">
                          <Search size={14} className="metadata-search-icon" />
                          <input
                            type="text"
                            placeholder="Filter fields..."
                            aria-label="Filter extracted metadata fields"
                            value={metadataSearchTerm}
                            onChange={(e) => {
                              const nextSearchTerm = e.target.value;
                              setMetadataSearchTerm(nextSearchTerm);
                              updateWorkspaceState({ metadataSearchTerm: nextSearchTerm });
                            }}
                            className="metadata-search-input"
                          />
                          {metadataSearchTerm && (
                            <button
                              type="button"
                              className="metadata-search-clear"
                              onClick={() => {
                                setMetadataSearchTerm('');
                                updateWorkspaceState({ metadataSearchTerm: '' });
                              }}
                              title="Clear filter"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        <div className="metadata-controls">
                          <div className="view-mode-toggle">
                            <button
                              className={`view-mode-btn ${metadataViewMode === 'fields' ? 'active' : ''}`}
                              onClick={() => {
                                setMetadataViewMode('fields');
                                updateWorkspaceState({ metadataViewMode: 'fields' });
                                changeResultLink({ metadata: 'fields', output: 'segment' });
                              }}
                              title="Graphical view"
                            >
                              <List size={12} />
                            </button>
                            <button
                              className={`view-mode-btn ${metadataViewMode === 'json' ? 'active' : ''}`}
                              onClick={() => {
                                setMetadataViewMode('json');
                                updateWorkspaceState({ metadataViewMode: 'json' });
                                changeResultLink({ metadata: 'json', output: 'segment' });
                              }}
                              title="Raw JSON view"
                            >
                              <Code size={12} />
                            </button>
                          </div>
                          {metadataViewMode === 'fields' && (
                            <>
                              <button
                                className={`sort-btn active`}
                                onClick={handleSortToggle}
                                title={`Sort alphabetically ${metadataSortDir === 'asc' ? '(A-Z)' : '(Z-A)'}`}
                              >
                                <SortAsc size={12} style={{
                                  transform: metadataSortDir === 'desc' ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.2s ease'
                                }} />
                                {metadataSortDir === 'asc' ? 'A-Z' : 'Z-A'}
                              </button>
                              <button
                                className="expand-collapse-btn"
                                onClick={handleToggleAllFields}
                                title={allFieldsExpanded ? 'Collapse all fields' : 'Expand all fields'}
                              >
                                {allFieldsExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {metadataViewMode === 'json' ? (
                        <div className="metadata-json-view">
                          <pre>{JSON.stringify(selectedSegment.metadata, null, 2)}</pre>
                        </div>
                      ) : (
                        <div className="metadata-fields-view">
                          <StructuredMetadataView
                            value={selectedSegment.metadata}
                            isFieldMatched={(path, tokens) => localMatchedFields.segment.has(path) || segmentSearchFieldMatcher(path, tokens)}
                            getFieldScore={selectedSegmentHit ? (path, tokens) => getBestFieldScoreForDisplayPath(selectedSegmentHit.field_scores || {}, selectedSegmentHit.field_instance_scores || {}, path, tokens) : undefined}
                            formatFieldScore={formatSearchScore}
                            collapsedPaths={collapsedMetadataPaths}
                            onTogglePath={toggleMetadataPath}
                            sortDirection={metadataSortDir}
                            filterTerm={metadataSearchTerm}
                            highlightedFieldPath={highlightFieldKey}
                            highlightedRowRef={highlightedMetadataRowRef}
                          />
                        </div>
                      )}
                    </div>
                  );
                } else if (segmentHasFailureEvidence) {
                  return (
                    <div className="metadata-display">
                      <p className="metadata-empty">
                        {selectedSegment.failure_message || selectedSegment.error_message || 'Processing failed - no metadata available'}
                      </p>
                    </div>
                  );
                } else if (selectedSegmentStatus === 'successful') {
                  return (
                    <div className="metadata-display">
                      <p className="metadata-empty">No metadata available for this segment</p>
                    </div>
                  );
                } else if (selectedSegmentStatus === 'processing') {
                  return (
                    <div className="metadata-display">
                      <div className="loading-state">
                        <RefreshCw size={16} className="spinning" />
                        <span>Processing segment metadata...</span>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="metadata-display">
                      <div className="loading-state">
                        <RefreshCw size={16} className="spinning" />
                        <span>Processing segment metadata...</span>
                      </div>
                    </div>
                  );
                }
              })()}

              {selectedSegment.processing_warning && (
                <div className="warning-message">
                  <AlertCircle size={16} />
                  {selectedSegment.processing_warning}
                </div>
              )}

              {selectedSegment.error_message && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  {selectedSegment.error_message}
                </div>
              )}
            </div>
          ) : isLoading ? (
            <div className="segment-details">
              <div className="loading-state">
                <RefreshCw size={16} className="spinning" />
                <span>Loading segment details...</span>
              </div>
            </div>
          ) : segments.length > 0 ? (
            <div className="segment-details">
              <ResultPlaceholder title={selectedSegment && !selectedSegmentIsVisible ? 'Selected result hidden' : 'Select a segment'}>
                {selectedSegment && !selectedSegmentIsVisible
                  ? 'Choose a matching tile or clear your search and filters to view this result.'
                  : 'Choose a tile to view its extracted details.'}
              </ResultPlaceholder>
            </div>
          ) : null}
          </div>}
          </div>

          {/* Video-Level Output - shown at the end of modal */}
          {selectedRunHasVideoLevel && (
            <div className="video-level-panel">
              <div className="video-level-header">
                <span>Video-Level Output</span>
                {videoLevelDisplayStatus !== 'successful' && <span className={`video-level-status-badge status-${String(videoLevelDisplayStatus).toLowerCase()}`}>
                  {formatVideoLevelStatusLabel(videoLevelDisplayStatus)}
                </span>}
                {!videoOutputHidden && !videoLevelResultError && videoLevelResult?.status === 'successful' && (
                  <div className="view-mode-toggle" role="group" aria-label="Video output view">
                    <button
                      className={`view-mode-btn ${videoLevelViewMode === 'fields' ? 'active' : ''}`}
                      onClick={() => {
                        setVideoLevelViewMode('fields');
                        updateWorkspaceState({ videoLevelViewMode: 'fields' });
                        changeResultLink({ metadata: 'fields', output: 'video', segmentId: null });
                      }}
                      title="Graphical view"
                    >
                      <List size={12} />
                    </button>
                    <button
                      className={`view-mode-btn ${videoLevelViewMode === 'json' ? 'active' : ''}`}
                      onClick={() => {
                        setVideoLevelViewMode('json');
                        updateWorkspaceState({ videoLevelViewMode: 'json' });
                        changeResultLink({ metadata: 'json', output: 'video', segmentId: null });
                      }}
                      title="Raw JSON view"
                    >
                      <Code size={12} />
                    </button>
                  </div>
                )}
              </div>

              {videoOutputHidden ? (
                <ResultPlaceholder title="Video output hidden">
                  Clear your search or filters to view the whole-video result.
                </ResultPlaceholder>
              ) : isLoadingVideoLevelResult && !videoLevelResult ? (
                <div className="video-level-loading">
                  <RefreshCw size={14} className="spinning" />
                  <span>Loading video-level output...</span>
                </div>
              ) : videoLevelResultError ? (
                <div className="video-level-error">
                  <AlertCircle size={14} />
                  <span>{videoLevelResultError}</span>
                </div>
              ) : videoLevelResult?.status === 'successful' ? (
                <div className="video-level-content">
                  {videoLevelResult.processing_warning && (
                    <div className="video-level-note">
                      {videoLevelResult.processing_warning}
                    </div>
                  )}
                  <div className="video-level-block">
                    {videoLevelViewMode === 'json' ? (
                      <pre className="video-level-json">
                        {JSON.stringify(videoLevelResult.metadata || {}, null, 2)}
                      </pre>
                    ) : (
                      <div className="video-level-fields">
                        {Object.keys(videoLevelResult.metadata || {}).length > 0 ? (
                          <StructuredMetadataView
                            value={videoLevelResult.metadata}
                            isFieldMatched={(path, tokens) => localMatchedFields.video.has(path) || videoSearchFieldMatcher(path, tokens)}
                            getFieldScore={selectedVideoHit ? (path, tokens) => getBestFieldScoreForDisplayPath(selectedVideoHit.field_scores || {}, selectedVideoHit.field_instance_scores || {}, path, tokens) : undefined}
                            formatFieldScore={formatSearchScore}
                            collapsedPaths={collapsedVideoLevelPaths}
                            onTogglePath={toggleVideoLevelMetadataPath}
                            sortDirection="asc"
                          />
                        ) : (
                          <div className="video-level-empty-fields">No structured metadata</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="video-level-empty">
                  {videoLevelResult?.error_message
                    || videoLevelProjection?.error_message
                    || (
                      videoLevelProjection?.status === 'processing'
                        ? 'Video-level extraction is still running for this media item.'
                        : 'No video-level output available yet.'
                    )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {isEmbeddedPresentation
        && isPresentationVisible
        && embeddedHeaderActionsTarget
        && createPortal(
          <FeatureAction feature="export_lifecycle"><button
            type="button"
            className="export-video-btn playground-studio-export-button"
            onClick={handleDownloadVideoMetadata}
            title="Export the current result view as JSON"
            aria-label="Export metadata"
            disabled={!canDownloadVideoMetadata}
          >
            <Download size={16} />
            <span>Export metadata</span>
          </button></FeatureAction>,
          embeddedHeaderActionsTarget,
        )}
    </>
  );
};

export default VideoDetailModal;
